/**
 * Referrals — clinician-portal referral analytics (P2 step 8, O-14).
 *
 * ADMIN-ONLY AND AGGREGATES-ONLY. The tab is gated in Reports and the endpoint
 * answers 403 for members; nothing here renders a patient, a referral row, or
 * a drill-down to one — the counts are the whole product. SMS failure CODES
 * come pre-extracted from the server (the raw carrier sentence embeds the
 * patient's phone number, review §B16) and only the code is ever rendered.
 *
 * The volume tables tolerate either `[{key,label,count}]` rows or a plain
 * `{label: count}` record — the endpoint is being built alongside this UI, so
 * the reader absorbs reasonable shape variation the way portalReferrals does.
 */
import { useEffect, useState } from 'react';
import api from '../../../services/api';
import BarRow from './BarRow';
import { SummaryStat } from './StatBlocks';

interface ReferralsProps {
  from: string | null;
  to: string | null;
}

interface CodeCount { code: string | number; count: number }

interface ReferralStats {
  window?: unknown;
  volume?: {
    total?: number;
    bySource?: unknown;
    byOrg?: unknown;
    byProvider?: unknown;
    byStudy?: unknown;
    byCondition?: unknown;
  };
  funnel?: { created?: number; sent?: number; delivered?: number; viewed?: number; signed?: number; consented?: number };
  terminals?: { declined?: number; expired?: number; withdrawn?: number; collisionReview?: number; smsFailedNow?: number };
  timingHours?: {
    medianSentToDelivered?: number | null;
    medianDeliveredToViewed?: number | null;
    medianViewedToSigned?: number | null;
    medianCreatedToConsented?: number | null;
  };
  smsFailures?: { total?: number; byCode?: CodeCount[] };
  collisions?: { flagged?: number; ratePct?: number | null };
  conversion?: { withContact?: number; withPostConsentAttempt?: number };
  reminders?: { reminded?: number; signedAfterReminder?: number };
  expiry?: { expired?: number; purged?: number };
}

const n = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Hours to one decimal; em-dash for null/absent medians. */
const fmtHours = (v: number | null | undefined): string =>
  v === null || v === undefined || !Number.isFinite(Number(v)) ? '—' : `${Number(v).toFixed(1)} h`;

/**
 * A volume bucket as label/count rows, whatever shape the server sent —
 * an array of objects (any reasonable key/label/count field names) or a
 * plain record. Sorted by count, descending.
 */
function asRows(v: unknown): { label: string; count: number }[] {
  let rows: { label: string; count: number }[] = [];
  if (Array.isArray(v)) {
    rows = v.map((r: any) => ({
      label: String(
        r?.label ?? r?.name ?? r?.title ?? r?.source ?? r?.org ?? r?.practice ?? r?.provider ?? r?.study ??
        r?.condition ?? r?.key ?? '—',
      ),
      count: n(Number(r?.count ?? r?.total ?? 0)),
    }));
  } else if (v && typeof v === 'object') {
    rows = Object.entries(v as Record<string, unknown>).map(([label, count]) => ({ label, count: n(Number(count)) }));
  }
  return rows.sort((a, b) => b.count - a.count);
}

/** A dense label/count table inside the standard card. */
function VolumeTable({ title, rows, caption }: { title: string; rows: { label: string; count: number }[]; caption?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="border-b border-gray-100 px-4 py-2.5 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {caption && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{caption}</p>}
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-sm text-gray-400">—</p>
      ) : (
        <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-700">
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.map((r, i) => (
              <tr key={`${r.label}-${i}`}>
                <td className="min-w-0 px-4 py-1.5 text-sm text-gray-700 dark:text-gray-200">
                  <span className="block max-w-[18rem] truncate" title={r.label}>{r.label}</span>
                </td>
                <td className="px-4 py-1.5 text-right text-sm tabular-nums text-gray-500 dark:text-gray-400">
                  {r.count.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const FUNNEL_STEPS: { key: keyof NonNullable<ReferralStats['funnel']>; label: string }[] = [
  { key: 'created', label: 'Created' },
  { key: 'sent', label: 'Sent' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'viewed', label: 'Viewed' },
  { key: 'signed', label: 'Signed' },
  { key: 'consented', label: 'Consented' },
];

export default function Referrals({ from, to }: ReferralsProps) {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/api/crm/analytics/referrals', { params: { from: from || undefined, to: to || undefined } })
      .then((res) => { if (alive) { setStats(res.data?.stats || null); setError(null); } })
      .catch((err) => {
        if (alive) {
          setError(err?.response?.status === 403
            ? 'Referral analytics is admin-only.'
            : err?.response?.data?.error || 'Failed to load referral analytics');
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [from, to]);

  if (loading) return <p className="p-4 text-sm text-gray-400">Loading referral analytics…</p>;
  if (error) return <p className="p-4 text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!stats) return <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No referral data yet.</p>;

  const funnel = stats.funnel || {};
  const terminals = stats.terminals || {};
  const timing = stats.timingHours || {};
  const collisions = stats.collisions || {};
  const conversion = stats.conversion || {};
  const reminders = stats.reminders || {};
  const expiry = stats.expiry || {};
  const volume = stats.volume || {};
  const smsFailures = stats.smsFailures || {};

  const total = n(volume.total) || n(funnel.created);
  if (total === 0 && n(funnel.created) === 0) {
    return <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No referrals in this range yet.</p>;
  }

  const funnelMax = Math.max(...FUNNEL_STEPS.map((s) => n(funnel[s.key])), 1);
  const created = n(funnel.created);
  const failureRows = Array.isArray(smsFailures.byCode) ? smsFailures.byCode : [];

  return (
    <div className="space-y-4">
      {/* (a) The referral funnel — each stage as a share of created. */}
      <div className="max-w-xl rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Referral funnel</h3>
          <span className="flex-shrink-0 text-xs text-gray-400">{total.toLocaleString()} referral{total === 1 ? '' : 's'}</span>
        </div>
        <div className="space-y-0.5">
          {FUNNEL_STEPS.map(({ key, label }) => {
            const count = n(funnel[key]);
            const hint = key !== 'created' && count > 0 && created > 0
              ? `${Math.round((count / created) * 100)}%`
              : null;
            return <BarRow key={key} label={label} count={count} max={funnelMax} hint={hint} />;
          })}
        </div>
      </div>

      {/* (b) Terminal states + collisions + reminders + expiry + conversion. */}
      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-5">
        <SummaryStat label="Declined" value={n(terminals.declined).toLocaleString()} />
        <SummaryStat label="Expired" value={n(terminals.expired).toLocaleString()} />
        <SummaryStat label="Withdrawn (STOP)" value={n(terminals.withdrawn).toLocaleString()} />
        <SummaryStat label="In collision review" value={n(terminals.collisionReview).toLocaleString()} sub="name mismatch, unresolved" />
        <SummaryStat label="Failed texts" value={n(terminals.smsFailedNow).toLocaleString()} sub="sitting unsent right now" />
        <SummaryStat
          label="Collisions flagged"
          value={n(collisions.flagged).toLocaleString()}
          sub={collisions.ratePct === null || collisions.ratePct === undefined
            ? '—'
            : `${Number(collisions.ratePct).toFixed(1)}% of referrals`}
        />
        <SummaryStat
          label="Reminded"
          value={n(reminders.reminded).toLocaleString()}
          sub={`${n(reminders.signedAfterReminder).toLocaleString()} signed after a reminder`}
        />
        <SummaryStat
          label="Expiry"
          value={n(expiry.expired).toLocaleString()}
          sub={`${n(expiry.purged).toLocaleString()} purged`}
        />
        <SummaryStat label="Became a CRM contact" value={n(conversion.withContact).toLocaleString()} sub="of consented referrals" />
        <SummaryStat
          label="Contacted after consent"
          value={n(conversion.withPostConsentAttempt).toLocaleString()}
          sub="a call or text attempt exists"
        />
      </div>

      {/* (c) Timing medians. */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Timing <span className="font-normal text-gray-400">(median hours)</span></h3>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryStat label="Sent → delivered" value={fmtHours(timing.medianSentToDelivered)} />
          <SummaryStat label="Delivered → viewed" value={fmtHours(timing.medianDeliveredToViewed)} />
          <SummaryStat label="Viewed → signed" value={fmtHours(timing.medianViewedToSigned)} />
          <SummaryStat label="Created → consented" value={fmtHours(timing.medianCreatedToConsented)} />
        </div>
      </div>

      {/* (d) Volume by source / study / condition. */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <VolumeTable title="By source" rows={asRows(volume.bySource)} />
        <VolumeTable title="By study" rows={asRows(volume.byStudy)} />
        <VolumeTable title="By condition" rows={asRows(volume.byCondition)} />
      </div>

      {/* (e) By practice + by provider. The caption is ON THE FACE of the
          section (§4.8/S-36): a funnel silently excluding the fax channel
          reads as "practices are not referring". */}
      <div className="space-y-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">By practice &amp; provider</h3>
          <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
            Portal referrals only — fax and phone referrals are not attributed to a practice or clinician.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <VolumeTable title="By practice" rows={asRows(volume.byOrg)} />
          <VolumeTable title="By provider" rows={asRows(volume.byProvider)} />
        </div>
      </div>

      {/* (f) SMS failure codes — codes only, never the carrier sentence. */}
      <div className="max-w-xl rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-2.5 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Failed text codes</h3>
          <span className="text-xs text-gray-400">{n(smsFailures.total).toLocaleString()} failure{n(smsFailures.total) === 1 ? '' : 's'}</span>
        </div>
        {failureRows.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">No carrier failures in this range.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-700">
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {failureRows.map((r, i) => (
                <tr key={`${r.code}-${i}`}>
                  <td className="px-4 py-1.5 font-mono text-xs text-gray-700 dark:text-gray-200">{String(r.code)}</td>
                  <td className="px-4 py-1.5 text-right text-sm tabular-nums text-gray-500 dark:text-gray-400">
                    {n(r.count).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
